import React from 'react';

import { useSafeOnboarding as useOperatorSafeOnboarding } from '@contexts/operator/OnboardingContext';
import { useSafeOnboarding as useAdminSafeOnboarding } from '@contexts/admin/OnboardingContext';
import Can from '@shared/permissions/Can';
import { Button } from 'antd';
import type { SizeType } from 'antd/es/config-provider/SizeContext';
import type { ReactNode, RefObject } from 'react';
import { useTranslation } from 'react-i18next';

import type { Permission } from '@/permissions_manifest.generated';

// Layout constants - 集中管理避免 magic numbers
export const FLOATING_ACTION_BAR = {
  SIDEBAR_WIDTH: 208,
  DRAWER_WIDTH: 450,
  MARGIN: 32,
  HEIGHT: 64,
  get DRAWER_OFFSET() {
    return this.DRAWER_WIDTH + this.MARGIN; // 482
  }
};

interface FloatingActionBarProps {
  role?: 'admin' | 'operator';
  onCancel?: () => void;
  onSubmit?: () => void;
  loading?: boolean;
  submitText?: string;
  cancelText?: string;
  extraButtons?: ReactNode;
  className?: string;
  submitRef?: RefObject<HTMLElement | null> | null;
  size?: 'small' | 'middle' | 'large' | 'default';
  submitIcon?: ReactNode;
  disabled?: boolean;
  submitRequiredPermission?: Permission;
}

/**
 * FloatingActionBar - 懸浮底部按鈕列
 *
 * 統一的表單操作按鈕列，固定在頁面底部，會自動避開 OnboardingDrawer
 *
 * @param {Object} props
 * @param {string} props.role - 角色 ('admin' 或 'operator')，用於判斷 OnboardingContext
 * @param {Function} props.onCancel - 取消按鈕的回調函數
 * @param {Function} props.onSubmit - 提交按鈕的回調函數
 * @param {boolean} props.loading - 提交按鈕的 loading 狀態
 * @param {string} props.submitText - 提交按鈕的文字（預設為 common.button.save）
 * @param {string} props.cancelText - 取消按鈕的文字（預設為 common.button.cancel）
 * @param {React.ReactNode} props.extraButtons - 額外的按鈕（會放在取消和提交之間）
 * @param {string} props.className - 額外的 CSS class
 * @param {Object} props.submitRef - 提交按鈕的 ref
 * @param {React.ReactNode} props.submitIcon - 提交按鈕的圖示
 */
const FloatingActionBar: React.FC<FloatingActionBarProps> = ({
  role = 'admin',
  onCancel,
  onSubmit,
  loading = false,
  submitText,
  cancelText,
  extraButtons = null,
  className = '',
  submitRef = null,
  size = 'default',
  submitIcon = null,
  disabled = false,
  submitRequiredPermission
}) => {
  const { t } = useTranslation();

  // 兩個 context 都呼叫（hooks 不能條件式呼叫），再根據 role 選擇
  const operatorOnboarding = useOperatorSafeOnboarding();
  const adminOnboarding = useAdminSafeOnboarding();
  const showOnboarding = role === 'admin'
    ? adminOnboarding.showOnboarding
    : operatorOnboarding.showOnboarding;

  const rightOffset = showOnboarding
    ? FLOATING_ACTION_BAR.DRAWER_WIDTH
    : 0;

  return (
    <div
      className={`floating-action-bar ${className}`}
      style={{
        right: rightOffset
      }}
    >
      {onCancel && (
        <Button size={size as SizeType} onClick={onCancel}>
          {cancelText || t('common.button.cancel')}
        </Button>
      )}
      {extraButtons}
      {submitRequiredPermission ? (
        <Can requiredPermission={submitRequiredPermission}>
          <Button
            ref={submitRef as RefObject<HTMLButtonElement>}
            data-testid="floating-action-submit-btn"
            type="primary"
            size={size as SizeType}
            icon={submitIcon}
            loading={loading}
            disabled={disabled}
            onClick={onSubmit}
          >
            {submitText || t('common.button.save')}
          </Button>
        </Can>
      ) : (
        <Button
          ref={submitRef as RefObject<HTMLButtonElement>}
          data-testid="floating-action-submit-btn"
          type="primary"
          size={size as SizeType}
          icon={submitIcon}
          loading={loading}
          disabled={disabled}
          onClick={onSubmit}
        >
          {submitText || t('common.button.save')}
        </Button>
      )}
    </div>
  );
};

export default FloatingActionBar;

/**
 * FloatingActionBarSpacer - 底部留白元件
 *
 * 用於在表單底部加入留白，避免內容被懸浮按鈕遮擋
 */
export function FloatingActionBarSpacer() {
  return <div style={{ height: FLOATING_ACTION_BAR.HEIGHT }} />;
}
